import React, { useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Upload, ImageIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '../../ui/dialog';
import {
  AWS_BUCKET_MMS,
  S3_GET_PRESIGNED_URLS
} from '../../../utils/constants';

const ImageUploader = ({ setImageData }) => {
  const [preview, setPreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [uploadErrorModalOpen, setUploadErrorModalOpen] = useState(false);
  const fileInputRef = useRef(null);

  const uploadToS3 = async (file, originalURL) => {
    setUploading(true);

    const fileType = file.type;
    const fileName = `${uuidv4()}.${file.name.split('.').pop()}`;

    try {
      // 1. Obtener la URL prefirmada desde tu API Gateway
      const presignedResponse = await fetch(S3_GET_PRESIGNED_URLS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          bucketName: AWS_BUCKET_MMS,
          files: [{ fileName, fileType }]
        })
      });

      if (!presignedResponse.ok) {
        throw new Error('Failed to get presigned URL');
      }

      const { urls } = await presignedResponse.json();
      const presignedUrl = urls?.[0]?.url;

      if (!presignedUrl) {
        throw new Error('No presigned URL returned');
      }

      // 2. Subir el archivo directamente a S3
      const uploadResult = await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': fileType
        },
        body: file
      });

      if (!uploadResult.ok) {
        throw new Error('File upload to S3 failed');
      }

      // 3. Actualizar UI con URL subida
      const uploadedFileUrl = `https://${AWS_BUCKET_MMS}.s3.amazonaws.com/${fileName}`;

      setPreview(uploadedFileUrl);
      setImageUrl(uploadedFileUrl);
      setImageData({
        originalURL: originalURL || uploadedFileUrl,
        text: file.name || fileName,
        url: uploadedFileUrl
      });

      console.log(`File uploaded successfully: ${uploadedFileUrl}`);
    } catch (error) {
      console.error(error);
      setUploadErrorModalOpen(true);
    }

    setUploading(false);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setPreview(URL.createObjectURL(file));
      uploadToS3(file, null);
    }
  };

  const handleUrlChange = async (event) => {
    const inputUrl = event.target.value;
    setImageUrl(inputUrl);
    setPreview(inputUrl);

    // Actualizar imageData inmediatamente con la URL ingresada
    setImageData({
      originalURL: inputUrl,
      text: '',
      url: inputUrl
    });

    // Solo intentar subir si la URL parece válida
    if (!inputUrl.trim() || !inputUrl.startsWith('http')) {
      return;
    }

    try {
      setUploading(true);
      const response = await axios.get(inputUrl, { responseType: 'blob' });

      const file = new File([response.data], `${uuidv4()}.jpg`, {
        type: response.headers['content-type']
      });

      uploadToS3(file, inputUrl);
    } catch (error) {
      console.error('Error fetching image from URL:', error);
      setUploading(false);
      // No mostramos error aquí para permitir que el usuario siga escribiendo
    }
  };

  return (
    <>
      <div className="border border-border rounded-2xl overflow-hidden bg-card">
        <div className="aspect-video bg-muted/30 flex items-center justify-center overflow-hidden relative">
          {preview ? (
            <img
              src={preview}
              alt="Preview"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="text-center text-muted-foreground p-6">
              <ImageIcon className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-base">No image preview</p>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-base font-medium">Image URL</Label>
        <Input
          type="text"
          value={imageUrl}
          onChange={handleUrlChange}
          placeholder="Enter image URL"
          className="h-12 text-base"
        />
      </div>

      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        className="hidden"
        onChange={handleFileChange}
      />

      <Button
        type="button"
        variant="outline"
        className="w-full h-12 gap-2"
        onClick={() => fileInputRef.current?.click()}>
        <Upload className="h-5 w-5" />
        Upload Image
      </Button>

      {uploading && (
        <p className="text-sm text-muted-foreground text-center">
          Uploading...
        </p>
      )}

      {/* Error Modal */}
      <Dialog
        open={uploadErrorModalOpen}
        onOpenChange={setUploadErrorModalOpen}>
        <DialogContent className="sm:max-w-md text-center py-10">
          <DialogHeader className="items-center">
            <DialogTitle className="text-2xl font-bold text-foreground">
              Error!
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              Failed to upload image
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-4">
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
              onClick={() => setUploadErrorModalOpen(false)}>
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImageUploader;
